<template>
  <v-autocomplete
    v-model="donationCause"
    v-model:search="query"
    :items="displayedCauses"
    :cache-items="false"
    :clearable="clearable"
    :disabled="disabled"
    :hide-details="hideDetails"
    :hide-no-data="loading || query.length < 2"
    :loading="loading"
    :placeholder="t('donationDetails.form.donationCauseId.placeholder')"
    :rules="rules"
    :item-title="(item) => item.name || ''"
    :item-value="(item) => item"
    :label="labelText"
    :no-data-text="t('donationDetails.form.donationCauseId.autoComplete.noDataText')"
    no-filter
    return-object
    :menu-props="{ contentClass: 'custom-autocomplete-menu' }"
  >
    <template #item="{ item, props }">
      <v-list-item v-if="item.value.header" disabled>
        <v-list-item-title class="font-weight-bold text-primary">{{
          item.value.header
        }}</v-list-item-title>
      </v-list-item>
      <v-list-item v-else v-bind="{ ...props, title: '' }">
        <template v-if="item.value.logoUrl && item.value.logoUrl.length > 0" #prepend>
          <v-avatar size="40">
            <v-img
              :src="item.value.logoUrl"
              :alt="
                t('donationDetails.form.donationCauseId.autoComplete.logoAltText', {
                  causeName: item.value.name,
                })
              "
            />
          </v-avatar>
        </template>
        <v-list-item-title>{{ item.value.name }}</v-list-item-title>
        <v-list-item-subtitle>
          <span
            v-if="
              item.value.organizationGroup && item.value.organizationGroup.name !== item.value.name
            "
            >{{
              t('donationDetails.form.donationCauseId.autoComplete.fundName', {
                fundName: item.value.organizationGroup.name,
              })
            }}
            |
          </span>
          <span
            v-if="item.value.organizationGroup && item.value.organizationGroup.giro"
            class="font-weight-medium"
            >{{ item.value.organizationGroup.giro }}</span
          >
        </v-list-item-subtitle>
      </v-list-item>
    </template>

    <template #selection="{ item }">
      <div class="d-flex align-center">
        <v-avatar size="40" class="mr-2">
          <v-img
            v-if="item.value.logoUrl && item.value.logoUrl.length > 0"
            :src="item.value.logoUrl"
            :alt="item.value.name"
          />
          <v-icon v-else size="40">mdi-charity</v-icon>
        </v-avatar>
        <span>{{ item.value.name }}</span>
      </div>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, nextTick } from 'vue';
import store from '@/store';
import { ENDPOINTS, get, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { DonationCause } from '@/interfaces/organizationgroup/donationCause';
import { getUserCountry } from '@/utils/userCountry';
// prefer useLabel composable for translations
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  props: {
    disabled: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    hideDetails: { type: Boolean, default: true },
    onlyActive: { type: Boolean, default: true },
    label: { type: String, default: '' },
    hideLabel: { type: Boolean, default: false },
    persistentPlaceholder: { type: Boolean, default: false },
    modelValue: { type: Object as () => DonationCause, default: null },
    rules: { type: Array as () => ((value) => string | boolean)[], default: () => [] },
    defaultDonationCauses: { type: Array as () => DonationCause[], default: () => [] },
    disableInfiniteScroll: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    // use top-level store instead of instance proxy
    const { t } = useLabel();

    const donationCauses = ref([] as DonationCause[]);
    const paginatedCauses = ref([] as DonationCause[]);
    const initialCauses = ref([] as ({ header: string } | DonationCause)[]);
    const loading = ref(false);
    const setInitial = ref(false);
    const query = ref('');
    const timerId = ref(0);
    const page = ref(1);
    const limit = ref(50);
    const totalPaginatedAvailable = ref(0);
    const scrollerAttached = ref(false);

    const donationCause = computed<DonationCause | null>({
      get: () => props.modelValue || null,
      set: (newValue: DonationCause | null) => {
        emit('update:modelValue', newValue);
        emit('change', newValue);
      },
    });

    const labelText = computed(() =>
      props.hideLabel
        ? ''
        : props.label || (t('donationDetails.form.donationCauseId.autoComplete.fund') as string)
    );

    const displayedCauses = computed(() => {
      if (!query.value || query.value.length < 2) {
        // Only show default donation causes when no search query
        const combined: ({ header: string } | DonationCause)[] = [];

        // Add default donation causes first if provided
        if (props.defaultDonationCauses && props.defaultDonationCauses.length > 0) {
          combined.push(...props.defaultDonationCauses);
        }

        return combined;
      }
      return donationCauses.value || [];
    });

    const recipientId = computed(() => {
      return store.state.requestSession?.donationCases?.[0]?.recipient?.id ?? null;
    });

    const fetchCauses = async (endpoint: string, params) => {
      try {
        const response = await get(endpoint, params);
        return response || {};
      } catch (error) {
        window.log?.error('[Guide] donationCauses.autocomplete — Failed to fetch donation causes', {
          message: guideErrorMessage(error),
          httpStatus: guideHttpStatus(error),
          endpoint: endpoint.split('?')[0],
        });
        // Return empty structure instead of throwing to prevent component crashes
        return {
          curatedCauseResult: { globalCauses: [], tenantCauses: [] },
          paginatedResponse: { data: [], total: 0 },
        };
      }
    };

    const loadCauses = async (endpoint: string, extraParams = {}) => {
      const country = getUserCountry();
      const params = {
        onlyActive: props.onlyActive,
        country,
        ...extraParams,
      };
      return await fetchCauses(endpoint, params);
    };

    // Commented out - no longer loading initial causes for search-only functionality
    /*
    const getInitialCauses = async () => {
      const response = await loadCauses(ENDPOINTS.INITIAL_DONATION_CAUSES, {
        limit: 5,
        ...(recipientId.value != null && { recipientId: recipientId.value }),
      });
      const curated = response?.curatedCauseResult;
      const total =
        response?.paginatedResponse?.totalRowCount || response?.paginatedResponse?.total || 0;
      if (total > 0) {
        totalPaginatedAvailable.value = total;
      }
      const initialCausesList: ({ header: string } | DonationCause)[] = [];

      const addCauses = (header: string, causes?: DonationCause[]) => {
        const valid = causes?.filter((c) => c?.name) || [];
        if (valid.length) initialCausesList.push({ header }, ...valid);
      };

      addCauses(
        t('donationDetails.form.donationCauseId.autoComplete.popularCausesHeader') as string,
        curated?.globalCauses
      );
      addCauses(
        t('donationDetails.form.donationCauseId.autoComplete.tenantCausesHeader') as string,
        curated?.tenantCauses
      );
      initialCauses.value = initialCausesList;
    };

    const getPaginatedCauses = async () => {
      if (loading.value || paginatedCauses.value.length >= totalPaginatedAvailable.value) {
        return;
      }
      loading.value = true;
      const response = await loadCauses(ENDPOINTS.INITIAL_DONATION_CAUSES, {
        limit: limit.value,
        page: page.value,
      });
      const newCauses = response?.paginatedResponse?.data || [];
      paginatedCauses.value.push(...newCauses);
      page.value += 1;
      const total = response?.paginatedResponse?.total;
      if (total) totalPaginatedAvailable.value = total;
      loading.value = false;
    };
    */

    const getDonationCauses = async (queryStr: string) => {
      clearTimeout(timerId.value);
      if (queryStr.length <= 1) return;
      timerId.value = window.setTimeout(async () => {
        loading.value = true;
        const results = await loadCauses(
          `${ENDPOINTS.DONATION_CAUSES}/${ENDPOINTS.SEARCH}/${queryStr}`
        );
        donationCauses.value = (results || []).filter((d) => d.name);
        loading.value = false;
      }, 250);
    };

    const pushSelected = (selected: DonationCause) => {
      if (!donationCauses.value.find((c) => c.id === selected.id)) {
        donationCauses.value.push(selected);
        query.value = selected.name;
      }
    };

    // Commented out - no longer using scroll functionality for search-only component
    /*
    const attachScrollListener = () => {
      // Use the specific selector that works: the v-list containing the causes
      const menuContent = document.querySelector('.v-list') as HTMLElement;

      if (
        menuContent &&
        menuContent.scrollHeight > menuContent.clientHeight &&
        !menuContent.dataset.listenerAttached
      ) {
        menuContent.addEventListener('scroll', onVirtualScroll);
        menuContent.dataset.listenerAttached = 'true';
        scrollerAttached.value = true;
      } else if (!menuContent) {
        // No v-list element found
      } else if (menuContent.dataset.listenerAttached) {
        scrollerAttached.value = true;
      } else {
        // v-list element found but not scrollable
      }
    };
    */

    // Commented out - no longer using scroll functionality for search-only component
    /*
    const onFocus = () => {
      // Always try to attach scroll listener when dropdown is focused
      // Reset the attached flag to allow reattachment
      scrollerAttached.value = false;

      // Poll every 100ms until menu is found and listener attached
      let pollCount = 0;
      const maxPolls = 50; // Stop after 5 seconds

      const pollForMenu = () => {
        if (scrollerAttached.value) return; // Already attached
        if (pollCount >= maxPolls) {
          return;
        }

        pollCount++;
        attachScrollListener();

        if (!scrollerAttached.value) {
          // Keep polling if not attached yet
          setTimeout(pollForMenu, 100);
        }
      };

      // Start polling immediately
      pollForMenu();
    };
    */

    // Commented out - no longer using scroll functionality for search-only component
    /*
    const onVirtualScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollThreshold = 50;
      const currentScroll = target.scrollTop + target.clientHeight;
      const scrollTrigger = target.scrollHeight - scrollThreshold;
      const shouldLoad =
        currentScroll >= scrollTrigger &&
        !loading.value &&
        paginatedCauses.value.length < totalPaginatedAvailable.value;

      if (shouldLoad) {
        getPaginatedCauses();
      }
    };
    */

    watch(query, (val) => {
      // If query becomes an object (due to Vuetify behavior), convert it to the name string
      if (val && typeof val === 'object' && val.name) {
        nextTick(() => {
          query.value = val.name;
        });
        return;
      }
      // Only trigger search if it's a string with sufficient length
      if (val && typeof val === 'string' && val.length > 1 && !setInitial.value) {
        getDonationCauses(val);
      }
      setInitial.value = false;
    });

    watch(donationCause, (val) => {
      if (val) {
        pushSelected(val);
        // Set query to name string when donationCause changes
        nextTick(() => {
          if (val.name) {
            query.value = val.name;
          }
        });
      }
    });

    watch(
      () => props.defaultDonationCauses,
      (newCauses) => {
        // If there's only one default cause, select it
        if (newCauses && newCauses.length === 1 && !donationCause.value) {
          donationCause.value = newCauses[0] as DonationCause;
        }
      },
      { immediate: true, deep: true }
    );

    // Commented out - no longer loading initial causes for search-only functionality
    /*
    onMounted(() => {
      getInitialCauses();
      // Load initial paginated causes after initial causes loaded
      setTimeout(() => {
        if (totalPaginatedAvailable.value > 0) {
          getPaginatedCauses();
        }
      }, 200);
    });

    // debug helpers removed to satisfy lint (were only used for manual testing)

    const onBlur = () => {
      // Reset the attached flag when dropdown closes so it can be reattached when reopened
      scrollerAttached.value = false;
    };
    */

    return {
      donationCauses,
      paginatedCauses,
      initialCauses,
      loading,
      setInitial,
      query,
      timerId,
      donationCause,
      labelText,
      displayedCauses,
      recipientId,
      getDonationCauses,
      pushSelected,
      t,
    };
  },
});
</script>

<style>
.custom-autocomplete-menu {
  width: 100%;
  max-width: 720px !important;
}

.autocomplete-item-hover:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
